/* eslint-disable sonarjs/cognitive-complexity */
import classNames from 'classnames';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import ReactCookies from 'react-cookies';
import { useSelector, useDispatch } from 'react-redux';
import { COOKIES } from '../../../../brands/default/constants';
import { DEAL_EXPRESS_BUY_BUTTON_COMPONENT } from '../../../../brands/default/content/components/deal';
import {
  LINK_CHECKOUT,
  LINK_SECOND_CHECKOUT,
} from '../../../../brands/default/links';
import { trackEvent, updateDod } from '../../../../helpers/analytics';
import { basketCreator } from '../../../../helpers/basketCreator';
import { getSelectValues } from '../../../../helpers/checkout';
import { getProductPurchaseCap } from '../../../../helpers/deals';
import errorTrackingService from '../../../../helpers/errorTrackingService';
import expressBuyService from '../../../../helpers/expressBuyService';
import giftsExpressBuyService from '../../../../helpers/giftsExpressBuyService';
import { redirectToURL } from '../../../../helpers/url';
import PurchaseContext from '../../../../providers/PurchaseProvider';
import ThemeContext from '../../../../providers/ThemeProvider';
import {
  createBasket,
  addBasketProduct,
} from '../../../../redux/actions/basket';
import { setLoadingState } from '../../../../redux/actions/loading';
import { LOADING_STATUS } from '../../../../redux/reducers/loading';
import PrimaryButton from '../../../_generic/button/PrimaryButton';
import CustomNativeSelect from '../../../_generic/custom-native-select/CustomNativeSelect';
import useToast from '../../../_generic/toast/UseToast';

const DealExpressBuyButton = ({
  buttonRef,
  deal = {},
  className = '',
  buttonType = 'other',
  isClaim = false,
}) => {
  const theme = useContext(ThemeContext);
  const userBasket = useSelector((state) => state.basket.basket);
  const locationStore = useSelector((state) => state.locations);
  const bt = ReactCookies.load(COOKIES.basketToken);
  const [quantity, setQuantity] = useState(1);
  const purchaseCap = getProductPurchaseCap(deal);
  const quantities = getSelectValues(1, purchaseCap);
  const dispatch = useDispatch();
  const router = useRouter();
  const toast = useToast();
  const [, setOrderId] = useContext(PurchaseContext);

  // add product to basket and allow the user to checkout as normal if error is recieved from expressBuy-api
  const expressBuyFailToCheckout = async (message) => {
    // show toastr message for the user showing express buy has failed
    if (!isClaim) {
      toast.addToast(message, 'toast-error');
    }
    deal.products[0].quantity = quantity;
    const products = basketCreator(deal, locationStore);

    // start swap to normal checkout system
    // express buy has failed, creating basket and redirecting user to basket
    const btAux =
      userBasket && userBasket.products
        ? await addBasketProduct(bt, products[0])
        : await createBasket(products, false);

    if (!btAux && !bt) {
      errorTrackingService.logError({
        error: 'No Basket Token',
        message: 'Basket token cannot be undefined.',
      });
    }

    // a few seconds for reading toast
    setTimeout(() => {
      if (bt || btAux) {
        redirectToURL(`${LINK_CHECKOUT}/${bt || btAux}`, router);
      } else {
        redirectToURL(`${LINK_CHECKOUT}`, router);
      }
    }, 3_000);
  };

  const expressBuySuccess = (orderId) => {
    dispatch(setLoadingState(LOADING_STATUS.READY));
    setOrderId(orderId);

    redirectToURL(`${LINK_SECOND_CHECKOUT}/benefits/${orderId}`, router);
  };

  const onClick = () => {
    trackEvent('click_buy');
    updateDod({ clickBuyType: `click_buy_${buttonType}` });

    if (isClaim) {
      giftsExpressBuyService.failToCheckout = expressBuyFailToCheckout;
      giftsExpressBuyService.success = expressBuySuccess;
      giftsExpressBuyService.setProgressTest = (message) =>
        dispatch(setLoadingState(LOADING_STATUS.LOADING, message));
      giftsExpressBuyService.initExpressBuy(deal, quantity);
    } else {
      expressBuyService.failToCheckout = expressBuyFailToCheckout;
      expressBuyService.success = expressBuySuccess;
      expressBuyService.setProgressTest = (message) =>
        dispatch(setLoadingState(LOADING_STATUS.LOADING, message));
      expressBuyService.initExpressBuy(deal, quantity);
    }
  };

  return (
    <>
      <div className="express-buy-button__wrapper">
        <PrimaryButton
          aria-label={DEAL_EXPRESS_BUY_BUTTON_COMPONENT.quickBuy}
          buttonRef={buttonRef}
          className={
            isClaim
              ? classNames('size-largest', className)
              : classNames('size-largest-mobile-only', 'italic', className)
          }
          onClick={onClick}
        >
          {isClaim
            ? DEAL_EXPRESS_BUY_BUTTON_COMPONENT.claim
            : DEAL_EXPRESS_BUY_BUTTON_COMPONENT.quickBuy}
        </PrimaryButton>
        {!isClaim && (
          <div className="express-buy-button__quantity">
            <label className="express-buy-button__quantity-label">
              <span>QTY: </span>
              <CustomNativeSelect
                className="primary"
                onChange={(event) =>
                  setQuantity(Number.parseInt(event.target.value, 10))
                }
                value={quantity}
              >
                {quantities.map((value) => {
                  return (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  );
                })}
              </CustomNativeSelect>
            </label>
          </div>
        )}
      </div>
      <style jsx>{`
        .express-buy-button__wrapper {
          z-index: 101;
        }
        .express-buy-button__wrapper {
          position: relative;
        }
        .express-buy-button__quantity-label {
          background-color: ${theme.colors.primary}33;
          color: ${theme.colors.primary};
          padding: 4px;
          margin: 0;
        }
        .express-buy-button__quantity {
          position: absolute;
          box-shadow: 0 0 6px #000000aa;
          display: inline-block;
          white-space: pre;
          background-color: #ffffff;
          border-radius: 6px 6px 0 0;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
        }
        @media (min-width: ${theme.breakpoints.smUp}) {
          .express-buy-button__quantity {
            position: relative;
            border-radius: 0 0 6px 6px;
            top: -8px;
          }
        }
      `}</style>
    </>
  );
};

DealExpressBuyButton.propTypes = {
  buttonRef: PropTypes.object,
  buttonType: PropTypes.string,
  className: PropTypes.string,
  deal: PropTypes.object,
  isClaim: PropTypes.bool,
};

export default DealExpressBuyButton;
